// utils/checkoutStateUtils.js
import { getItem, setItem, removeItem } from './localStorageUtils';
import * as Sentry from '@sentry/react';

const CHECKOUT_STATE_KEY = 'checkout_state';
const CUSTOMER_INFO_KEY = 'customer_info'; // Separate key for persistent customer data
const RECEIPT_KEY = 'checkout_receipt'; // The finished order's confirmation (#584)
const STATE_EXPIRATION_MS = 30 * 60 * 1000; // 30 minutes
const CUSTOMER_INFO_EXPIRATION_MS = 90 * 24 * 60 * 60 * 1000; // 90 days
// Long enough to cover a next-day order placed the morning before (#485),
// short enough that a stale receipt never greets a returning customer.
const RECEIPT_EXPIRATION_MS = 48 * 60 * 60 * 1000;

/**
 * Save checkout state to localStorage with timestamp
 * @param {Object} state - Checkout state to persist
 * @param {Object} state.customerInfo - Customer information
 * @param {string} state.pickupTime - Pickup time selection
 * @param {number} state.tipAmount - Tip amount in cents
 * @param {string} state.orderNotes - Order notes
 * @param {Object} state.orderResponse - Order response from Clover (if created)
 * @param {Object} state.paymentResponse - Payment response (if paid)
 */
export const saveCheckoutState = (state) => {
  try {
    const stateWithTimestamp = {
      ...state,
      timestamp: Date.now(),
    };

    setItem(CHECKOUT_STATE_KEY, stateWithTimestamp);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'checkoutStateUtils',
        action: 'saveCheckoutState',
      },
      extra: { state },
    });
    return false;
  }
};

/**
 * Load checkout state from localStorage
 * Returns null if state doesn't exist, is expired, or is invalid
 * @returns {Object|null} Checkout state or null
 */
export const loadCheckoutState = () => {
  try {
    const state = getItem(CHECKOUT_STATE_KEY, null);

    if (!state) {
      return null;
    }

    // Check if state has expired
    const now = Date.now();
    const age = now - (state.timestamp || 0);

    if (age > STATE_EXPIRATION_MS) {
      // State is expired, clean it up
      clearCheckoutState();
      return null;
    }

    // Remove timestamp before returning
    const { timestamp, ...checkoutState } = state;

    return checkoutState;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'checkoutStateUtils',
        action: 'loadCheckoutState',
      },
    });
    return null;
  }
};

/**
 * Clear checkout state from localStorage
 */
export const clearCheckoutState = () => {
  try {
    removeItem(CHECKOUT_STATE_KEY);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'checkoutStateUtils',
        action: 'clearCheckoutState',
      },
    });
    return false;
  }
};

/**
 * Check if there's a valid checkout session in progress
 * @returns {boolean} True if valid session exists
 */
export const hasActiveCheckoutSession = () => {
  const state = loadCheckoutState();
  return (
    state !== null &&
    (state.orderResponse?.order?.id || state.customerInfo?.email)
  );
};

/**
 * Get the current checkout step from persisted state
 * @returns {number} Current step (1, 2, or 3)
 */
export const getCheckoutStep = () => {
  const state = loadCheckoutState();

  if (!state) {
    return 1; // No state, start at step 1
  }

  if (state.paymentResponse) {
    return 3; // Payment complete
  }

  if (state.orderResponse?.order?.id) {
    return 2; // Order created, ready for payment
  }

  return 1; // Has customer info but no order yet
};

/**
 * Update specific fields in the persisted checkout state
 * @param {Object} updates - Fields to update
 */
export const updateCheckoutState = (updates) => {
  try {
    const currentState = loadCheckoutState() || {};
    const updatedState = {
      ...currentState,
      ...updates,
    };

    return saveCheckoutState(updatedState);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'checkoutStateUtils',
        action: 'updateCheckoutState',
      },
      extra: { updates },
    });
    return false;
  }
};

/**
 * Save customer info to localStorage separately (persists across orders)
 * @param {Object} customerInfo - Customer information to persist
 */
export const saveCustomerInfo = (customerInfo) => {
  try {
    const dataWithTimestamp = {
      ...customerInfo,
      timestamp: Date.now(),
    };

    setItem(CUSTOMER_INFO_KEY, dataWithTimestamp);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'checkoutStateUtils',
        action: 'saveCustomerInfo',
      },
      extra: { customerInfo },
    });
    return false;
  }
};

/**
 * Load customer info from localStorage
 * Returns null if doesn't exist, is expired, or is invalid
 * @returns {Object|null} Customer info or null
 */
export const loadCustomerInfo = () => {
  try {
    const data = getItem(CUSTOMER_INFO_KEY, null);

    if (!data) {
      return null;
    }

    // Check if data has expired (90 days)
    const now = Date.now();
    const age = now - (data.timestamp || 0);

    if (age > CUSTOMER_INFO_EXPIRATION_MS) {
      // Data is expired, clean it up
      clearCustomerInfo();
      return null;
    }

    // Remove timestamp before returning
    const { timestamp, ...customerInfo } = data;

    return customerInfo;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'checkoutStateUtils',
        action: 'loadCustomerInfo',
      },
    });
    return null;
  }
};

/**
 * Clear customer info from localStorage
 */
export const clearCustomerInfo = () => {
  try {
    removeItem(CUSTOMER_INFO_KEY);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        component: 'checkoutStateUtils',
        action: 'clearCustomerInfo',
      },
    });
    return false;
  }
};

/**
 * The receipt: what the confirmation screen renders, and nothing else (#584).
 *
 * Payment success clears the in-progress checkout state — correctly, the
 * order is done — which left the confirmation living only in React state. A
 * reload, or a mobile tab the OS evicted, sent the customer back to step 1
 * with no order number and no pickup time, possibly a day before they need
 * it. This snapshot outlives that state under its own key.
 *
 * It is deliberately narrow. Clover's payment response carries the
 * cardholder's name, billing address, BIN and expiry (#508 kept those out of
 * logs); the customer's own localStorage should not hold them either. Only
 * brand and last four survive, and no customer PII at all — the screen does
 * not show any.
 *
 * @param {Object} inputs
 * @param {Object} inputs.orderResponse - createOrder response
 * @param {Object} inputs.paymentResult - payOrder response
 * @param {string} inputs.pickupTime - 'asap' or an ISO timestamp
 * @param {number} inputs.tipAmount - the LIVE tip, in cents (#579)
 * @param {string} inputs.orderNotes
 * @returns {Object} the shape CheckoutContext restores from
 */
export const buildReceiptSnapshot = ({
  orderResponse,
  paymentResult,
  pickupTime,
  tipAmount,
  orderNotes,
}) => {
  const order = orderResponse?.order || {};
  const lineItems = (order.lineItems?.elements || []).map((line) => ({
    name: line.name,
    price: line.price,
    ...(line.unitQty != null ? { unitQty: line.unitQty } : {}),
    ...(line.modifications?.elements
      ? {
          modifications: {
            elements: line.modifications.elements.map((mod) => ({
              name: mod.name,
              amount: mod.amount,
            })),
          },
        }
      : {}),
  }));

  const payment = paymentResult?.payment || {};
  const source = payment.source
    ? { brand: payment.source.brand, last4: payment.source.last4 }
    : undefined;

  return {
    orderResponse: {
      order: {
        id: order.id,
        total: order.total,
        lineItems: { elements: lineItems },
      },
      checkoutDetails: { orderNotes: orderNotes || '' },
    },
    paymentResponse: {
      payment: {
        id: payment.id,
        amount: payment.amount,
        tipAmount: payment.tipAmount,
        ...(source ? { source } : {}),
      },
    },
    pickupTime,
    tipAmount: tipAmount || 0,
    orderNotes: orderNotes || '',
  };
};

/**
 * Persist the receipt (#584). Separate from the checkout state so the
 * session cleanup on payment success does not take the receipt with it.
 * @param {Object} receipt - from buildReceiptSnapshot
 */
export const saveReceipt = (receipt) => {
  try {
    setItem(RECEIPT_KEY, { ...receipt, savedAt: Date.now() });
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'checkoutStateUtils', action: 'saveReceipt' },
    });
    return false;
  }
};

/**
 * The stored receipt, or null when there is none or it has expired.
 * @returns {Object|null}
 */
export const loadReceipt = () => {
  try {
    const stored = getItem(RECEIPT_KEY, null);
    if (!stored) return null;

    if (Date.now() - (stored.savedAt || 0) > RECEIPT_EXPIRATION_MS) {
      clearReceipt();
      return null;
    }

    const { savedAt, ...receipt } = stored;
    return receipt;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'checkoutStateUtils', action: 'loadReceipt' },
    });
    return null;
  }
};

/** Forget the receipt: Return to Menu, or a new order has begun (#584). */
export const clearReceipt = () => {
  try {
    removeItem(RECEIPT_KEY);
    return true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'checkoutStateUtils', action: 'clearReceipt' },
    });
    return false;
  }
};
